// === Session Manager ===
// Extracted from app.ts — session open/switch, chat view reset, snapshot apply, loading state

import { deepClone } from '../utils';
import { send } from '../ws/connection';
import { showToast } from '../ui/toast';
import { sessionState, composeState } from '../state';
import { gitState } from '../state';
import {
  getLastSessionForAgent, setLastSessionForAgent, getSessionMeta,
  cacheSessionSnapshot, touchSessionCache, buildCachedSessionSnapshot, getSessionCacheDisposition,
  getAgentModeStorageKey,
} from './cache';
import { resetGitState, requestGitStatus } from '../ui/git';
import { logComposerFocusDebug, logSessionDebug, logSessionLoadDebug, logSessionFlowDebug, logSidebarSessionDebug } from '../ui/session-debug';
import { shouldSkipAutoComposerFocus } from '../ui/composer-focus';
import { getSessionRenderOptions } from './session-load-context';

// --- DI callbacks (injected by app.ts via setupSessionManager) ---
let _updateCwdBadge: (() => void) | null = null;
let _renderWorkspaceInsights: (() => void) | null = null;
let _updateAgentScopedUI: (() => void) | null = null;
let _setSelectedAgent: ((agent: string, opts?: any) => void) | null = null;
let _setCurrentAgent: ((agent: string) => void) | null = null;
let _getSavedModeForAgent: ((agent: string) => string) | null = null;
let _renderMessages: ((messages: any[], opts?: any) => void) | null = null;
let _ensureStreamingPlaceholderVisible: (() => void) | null = null;
let _renderPendingAttachments: (() => void) | null = null;
let _highlightActiveSession: (() => void) | null = null;
let _renderSessionList: (() => void) | null = null;
let _closeSidebar: (() => void) | null = null;
let _scrollToBottom: (() => void) | null = null;
let _buildWelcomeMarkup: ((agent: string) => string) | null = null;
let _setStatsDisplay: ((msg: any) => void) | null = null;
let _getVisibleSessions: (() => any[]) | null = null;
let _getSessionUpdatedTimestamp: ((session: any) => number) | null = null;
let _incrementRenderEpoch: (() => void) | null = null;
let _clearPreviewCode: (() => void) | null = null;
let _getSelectedAgent: (() => string) | null = null;

function logSessionSwitch(_event: string, _payload: Record<string, unknown>): void {}

function logSessionFlow(event: string, payload: Record<string, unknown>): void {
  logSessionFlowDebug(event, payload);
}

export function setupSessionManager(handlers: {
  updateCwdBadge: () => void;
  renderWorkspaceInsights: () => void;
  updateAgentScopedUI: () => void;
  setSelectedAgent: (agent: string, opts?: any) => void;
  setCurrentAgent: (agent: string) => void;
  getSavedModeForAgent: (agent: string) => string;
  renderMessages: (messages: any[], opts?: any) => void;
  ensureStreamingPlaceholderVisible: () => void;
  renderPendingAttachments: () => void;
  highlightActiveSession: () => void;
  renderSessionList: () => void;
  closeSidebar: () => void;
  scrollToBottom: () => void;
  buildWelcomeMarkup: (agent: string) => string;
  setStatsDisplay: (msg: any) => void;
  getVisibleSessions: () => any[];
  getSessionUpdatedTimestamp: (session: any) => number;
  incrementRenderEpoch: () => void;
  clearPreviewCode: () => void;
  getSelectedAgent: () => string;
}): void {
  _updateCwdBadge = handlers.updateCwdBadge;
  _renderWorkspaceInsights = handlers.renderWorkspaceInsights;
  _updateAgentScopedUI = handlers.updateAgentScopedUI;
  _setSelectedAgent = handlers.setSelectedAgent;
  _setCurrentAgent = handlers.setCurrentAgent;
  _getSavedModeForAgent = handlers.getSavedModeForAgent;
  _renderMessages = handlers.renderMessages;
  _ensureStreamingPlaceholderVisible = handlers.ensureStreamingPlaceholderVisible;
  _renderPendingAttachments = handlers.renderPendingAttachments;
  _highlightActiveSession = handlers.highlightActiveSession;
  _renderSessionList = handlers.renderSessionList;
  _closeSidebar = handlers.closeSidebar;
  _scrollToBottom = handlers.scrollToBottom;
  _buildWelcomeMarkup = handlers.buildWelcomeMarkup;
  _setStatsDisplay = handlers.setStatsDisplay;
  _getVisibleSessions = handlers.getVisibleSessions;
  _getSessionUpdatedTimestamp = handlers.getSessionUpdatedTimestamp;
  _incrementRenderEpoch = handlers.incrementRenderEpoch;
  _clearPreviewCode = handlers.clearPreviewCode;
  _getSelectedAgent = handlers.getSelectedAgent;
}

// --- lazy DOM helpers ---
function getChatTitle(): HTMLElement | null { return document.getElementById('chat-title'); }
function getSendBtn(): HTMLButtonElement | null { return document.getElementById('send-btn') as HTMLButtonElement | null; }
function getAbortBtn(): HTMLButtonElement | null { return document.getElementById('abort-btn') as HTMLButtonElement | null; }
function getModeSelect(): HTMLSelectElement | null { return document.getElementById('mode-select') as HTMLSelectElement | null; }
function getMessagesDiv(): Element | null { return document.getElementById('messages'); }
function getSessionLoadingOverlay(): HTMLElement | null { return document.getElementById('session-loading-overlay'); }
function getSessionLoadingLabelEl(): HTMLElement | null { return document.getElementById('session-loading-label'); }
function getMsgInput(): HTMLInputElement | null { return document.getElementById('msg-input') as HTMLInputElement | null; }

function focusComposerInput(): void {
  const msgInput = getMsgInput();
  logComposerFocusDebug('focus:requested', {
    hasInput: !!msgInput,
    disabled: !!msgInput?.disabled,
    skippedOnMobile: shouldSkipAutoComposerFocus(),
    activeElementId: document.activeElement instanceof HTMLElement ? document.activeElement.id || null : null,
  });
  if (shouldSkipAutoComposerFocus()) return;
  if (!msgInput || msgInput.disabled) return;
  requestAnimationFrame(() => {
    logComposerFocusDebug('focus:before-raf-focus', {
      connected: msgInput.isConnected,
      disabled: msgInput.disabled,
      activeElementId: document.activeElement instanceof HTMLElement ? document.activeElement.id || null : null,
    });
    if (!msgInput.isConnected || msgInput.disabled) return;
    msgInput.focus({ preventScroll: true });
    logComposerFocusDebug('focus:after-focus', {
      focused: document.activeElement === msgInput,
      activeElementId: document.activeElement instanceof HTMLElement ? document.activeElement.id || null : null,
    });
  });
}

function assistantMessageHasVisibleContent(message: any): boolean {
  if (!message || message.role !== 'assistant') return false;
  if (String(message.content || '').trim()) return true;
  const segments = Array.isArray(message.segments) ? message.segments : [];
  if (segments.some((segment: any) => {
    if (!segment) return false;
    if (segment.type === 'tool_group') return Array.isArray(segment.items) && segment.items.length > 0;
    if (segment.type === 'tool_call') return true;
    return !!String(segment.text || '').trim();
  })) return true;
  const toolCalls = Array.isArray(message.toolCalls) ? message.toolCalls : [];
  return toolCalls.length > 0;
}

// ============================================================
// Session running state
// ============================================================

export function setCurrentSessionRunningState(isRunning: boolean): void {
  const running = !!isRunning;
  const previousRunning = !!sessionState.currentSessionRunning;
  sessionState.currentSessionRunning = running;
  if (sessionState.currentSessionId) {
    let didUpdateMeta = false;
    sessionState.sessions = sessionState.sessions.map((session: any) => {
      if (session?.id !== sessionState.currentSessionId) return session;
      if (!!session.isRunning === running) return session;
      didUpdateMeta = true;
      return { ...session, isRunning: running };
    });
    if (didUpdateMeta) _renderSessionList?.();
  }
  if (previousRunning !== running) _renderWorkspaceInsights?.();
  _updateCwdBadge?.();
}

// ============================================================
// Initial session restore
// ============================================================

export function restoreInitialSession(agent?: string): void {
  const selectedAgent = _getSelectedAgent?.() || 'claude';
  const targetAgent = agent ? String(agent) : selectedAgent;
  logSessionFlow('restore-initial:start', {
    requestedAgent: agent || null,
    selectedAgent,
    targetAgent,
    currentSessionId: sessionState.currentSessionId,
  });
  _setSelectedAgent?.(targetAgent, { syncMode: true });
  _renderSessionList?.();

  const lastOpenedId = localStorage.getItem('webcoding-session');
  const lastOpenedMeta = lastOpenedId ? getSessionMeta(lastOpenedId) : null;
  if (lastOpenedMeta) {
    logSessionFlow('restore-initial:pick-last-opened', {
      sessionId: lastOpenedId,
      agent: lastOpenedMeta.agent || null,
      updated: lastOpenedMeta.updated || null,
    });
    openSession(lastOpenedId);
    return;
  }

  const lastSessionId = getLastSessionForAgent(targetAgent);
  const lastMeta = lastSessionId ? getSessionMeta(lastSessionId) : null;
  if (lastMeta && String(lastMeta.agent || '') === targetAgent) {
    logSessionFlow('restore-initial:pick-last-agent-session', {
      sessionId: lastSessionId,
      agent: lastMeta.agent || null,
      updated: lastMeta.updated || null,
    });
    openSession(lastSessionId);
    return;
  }

  // Fallback: open the most recently updated session in the list
  const allSessions = _getVisibleSessions?.() || [];
  if (allSessions.length > 0) {
    const mostRecent = allSessions.reduce((best: any, s: any) =>
      (_getSessionUpdatedTimestamp?.(s) || 0) > (_getSessionUpdatedTimestamp?.(best) || 0) ? s : best
    );
    logSessionFlow('restore-initial:pick-most-recent', {
      sessionId: mostRecent?.id || null,
      updated: mostRecent?.updated || null,
      count: allSessions.length,
    });
    openSession(mostRecent.id);
    return;
  }

  logSessionFlow('restore-initial:reset-empty', { targetAgent });
  resetChatView(targetAgent);
}

// ============================================================
// Reset chat view (new/empty session state)
// ============================================================

export function resetChatView(agent?: string): void {
  const selectedAgent = _getSelectedAgent?.() || 'claude';
  const baseAgent = String(agent || selectedAgent);
  logSessionFlow('reset-chat-view', {
    requestedAgent: agent || null,
    selectedAgent,
    baseAgent,
    previousSessionId: sessionState.currentSessionId,
    wasGenerating: composeState.isGenerating,
  });
  _setCurrentAgent?.(baseAgent);
  resetGitState();
  sessionState.currentSessionId = null;
  sessionState.loadedHistorySessionId = null;
  sessionState.currentHistoryTotal = 0;
  sessionState.currentHistoryBuffered = 0;
  sessionState.currentHistoryPending = false;
  sessionState.currentHistoryLoading = false;
  clearSessionLoading();
  setCurrentSessionRunningState(false);
  sessionState.currentCwd = null;
  sessionState.currentModel = '';
  sessionState.currentActiveRuntime = null;
  sessionState.currentRuntimeCount = 0;
  composeState.isGenerating = false;
  composeState.pendingText = '';
  composeState.pendingAttachments = [];
  composeState.uploadingAttachments = [];
  composeState.activeToolCalls.clear();
  _clearPreviewCode?.();
  const sendBtn = getSendBtn();
  const abortBtn = getAbortBtn();
  const modeSelect = getModeSelect();
  const chatTitle = getChatTitle();
  const messagesDiv = getMessagesDiv();
  if (sendBtn) sendBtn.hidden = false;
  if (abortBtn) abortBtn.hidden = true;
  const mode = _getSavedModeForAgent?.(baseAgent) || 'yolo';
  sessionState.currentMode = mode;
  if (modeSelect) modeSelect.value = mode;
  _updateAgentScopedUI?.();
  if (chatTitle) chatTitle.textContent = '新会话';
  _updateCwdBadge?.();
  if (messagesDiv) messagesDiv.innerHTML = _buildWelcomeMarkup?.(baseAgent) || '';
  _setStatsDisplay?.(null);
  _renderPendingAttachments?.();
  _highlightActiveSession?.();
  _renderWorkspaceInsights?.();
}

// ============================================================
// Apply session snapshot (load session into view)
// ============================================================

export function applySessionSnapshot(snapshot: any, options: any = {}): void {
  if (!snapshot) return;
  const closeSidebarAfterSnapshot = shouldCloseSidebarAfterSnapshot(snapshot, options);
  logSidebarSessionDebug('apply-snapshot-close-decision', {
    sessionId: snapshot.sessionId || null,
    currentSessionId: sessionState.currentSessionId || null,
    activeLoadSessionId: sessionState.activeSessionLoad?.sessionId || null,
    closeSidebarAfterSnapshot,
    optionCloseSidebarOnApply: !!options.closeSidebarOnApply,
    activeLoadCloseSidebarOnApply: !!sessionState.activeSessionLoad?.closeSidebarOnApply,
    sidebarWillClose: closeSidebarAfterSnapshot,
  });
  logSessionLoadDebug('load:apply-snapshot:start', {
    sessionId: snapshot.sessionId || null,
    currentSessionId: sessionState.currentSessionId || null,
    activeLoadSessionId: sessionState.activeSessionLoad?.sessionId || null,
    historyPending: !!snapshot.historyPending,
    complete: !!snapshot.complete,
    isRunning: !!snapshot.isRunning,
    messageCount: Array.isArray(snapshot.messages) ? snapshot.messages.length : 0,
    loadContextReason: options.loadContext?.reason || null,
    immediate: !!options.immediate,
    preserveStreaming: !!options.preserveStreaming,
    closeSidebarOnApply: closeSidebarAfterSnapshot,
    serverStateAuthoritative: snapshot.isRunning === false,
  });
  const isCurrentSession = snapshot.sessionId === sessionState.currentSessionId;
  const hasActiveStreamingState = isCurrentSession && (
    composeState.isGenerating || sessionState.currentSessionRunning
  );
  const preserveStreaming = !!(
    hasActiveStreamingState
    && snapshot.isRunning !== false
    && (options.preserveStreaming || sessionState.currentSessionRunning || snapshot.isRunning)
  );
  const sendBtn = getSendBtn();
  const abortBtn = getAbortBtn();
  const modeSelect = getModeSelect();
  const chatTitle = getChatTitle();
  if (composeState.isGenerating && !preserveStreaming) {
    logSessionFlow('apply-snapshot:reset-generating', {
      sessionId: snapshot.sessionId || null,
      snapshotIsRunning: !!snapshot.isRunning,
      currentSessionRunning: !!sessionState.currentSessionRunning,
      preserveStreaming,
      loadContextReason: options.loadContext?.reason || null,
    });
    composeState.isGenerating = false;
    if (sendBtn) sendBtn.hidden = false;
    if (abortBtn) abortBtn.hidden = true;
    composeState.pendingText = '';
    composeState.activeToolCalls.clear();
  }
  sessionState.currentSessionId = snapshot.sessionId;
  sessionState.loadedHistorySessionId = snapshot.sessionId;
  sessionState.currentHistoryTotal = Number.isFinite(snapshot.historyTotal) ? snapshot.historyTotal : ((snapshot.messages || []).length || 0);
  sessionState.currentHistoryBuffered = Number.isFinite(snapshot.historyBuffered) ? snapshot.historyBuffered : ((snapshot.messages || []).length || 0);
  sessionState.currentHistoryPending = !!snapshot.historyPending;
  sessionState.currentHistoryLoading = false;
  sessionState.currentHistoryRequestSeq = 0;
  document.dispatchEvent(new CustomEvent('webcoding-history-state-changed', {
    detail: {
      source: 'apply_session_snapshot',
      sessionId: sessionState.currentSessionId,
      historyTotal: sessionState.currentHistoryTotal,
      historyBuffered: sessionState.currentHistoryBuffered,
      historyPending: sessionState.currentHistoryPending,
      historyLoading: sessionState.currentHistoryLoading,
      loadContextReason: options.loadContext?.reason || null,
    },
  }));
  setLastSessionForAgent(snapshot.agent, sessionState.currentSessionId);
  if (chatTitle) chatTitle.textContent = snapshot.title || '新会话';
  _setCurrentAgent?.(snapshot.agent);
  setCurrentSessionRunningState(snapshot.isRunning === false ? false : preserveStreaming || snapshot.isRunning);
  _setStatsDisplay?.(snapshot);
  const nextGitCwd = snapshot.cwd || null;
  const gitCwdChanged = gitState.cwd !== nextGitCwd;
  sessionState.currentCwd = nextGitCwd;
  if (gitCwdChanged) resetGitState(nextGitCwd ? { cwd: nextGitCwd } : {});
  _updateCwdBadge?.();
  if (snapshot.mode) {
    sessionState.currentMode = snapshot.mode;
    if (modeSelect) modeSelect.value = snapshot.mode;
    localStorage.setItem(getAgentModeStorageKey(sessionState.currentAgent), snapshot.mode);
  }
  sessionState.currentModel = snapshot.model || '';
  sessionState.currentActiveRuntime = snapshot.activeRuntime ? deepClone(snapshot.activeRuntime) : null;
  sessionState.currentRuntimeCount = Number.isFinite(snapshot.runtimeCount) ? snapshot.runtimeCount : 0;
  _updateAgentScopedUI?.();
  if (!preserveStreaming) {
    _renderMessages?.(snapshot.messages || [], getSessionRenderOptions({
      immediate: !!options.immediate,
      loadContext: options.loadContext || null,
    }));
    const renderedMessages = Array.isArray(snapshot.messages) ? snapshot.messages : [];
    const lastMessage = renderedMessages[renderedMessages.length - 1] || null;
    const needsStreamingPlaceholder = !lastMessage
      || lastMessage.role !== 'assistant'
      || !assistantMessageHasVisibleContent(lastMessage);
    if (snapshot.isRunning && needsStreamingPlaceholder) {
      _ensureStreamingPlaceholderVisible?.();
    }
  }
  _highlightActiveSession?.();
  _renderSessionList?.();
  if (closeSidebarAfterSnapshot) _closeSidebar?.();
  // Failsafe: once the target session has rendered into view, do not leave the
  // blocking overlay hanging forever if the follow-up completion event is missed.
  if (sessionState.activeSessionLoad?.sessionId === snapshot.sessionId) {
    requestAnimationFrame(() => {
      if (sessionState.currentSessionId !== snapshot.sessionId) return;
      const messagesDiv = getMessagesDiv();
      const hasRenderedContent = !!messagesDiv && messagesDiv.children.length > 0;
      logSessionLoadDebug('load:apply-snapshot:failsafe-check', {
        sessionId: snapshot.sessionId || null,
        hasRenderedContent,
        activeLoadSessionId: sessionState.activeSessionLoad?.sessionId || null,
        currentSessionId: sessionState.currentSessionId || null,
      });
      if (hasRenderedContent) clearSessionLoading(snapshot.sessionId);
    });
  }
  _renderWorkspaceInsights?.();
  if (nextGitCwd && (gitCwdChanged || !gitState.status)) {
    requestGitStatus({ silent: true });
  }
  logSessionFlow('apply-snapshot:done', {
    sessionId: snapshot.sessionId,
    currentSessionId: sessionState.currentSessionId,
    currentAgent: sessionState.currentAgent,
    currentMode: sessionState.currentMode,
    currentCwd: sessionState.currentCwd,
    activeLoadSessionId: sessionState.activeSessionLoad?.sessionId || null,
  });
}

function shouldCloseSidebarAfterSnapshot(snapshot: any, options: any = {}): boolean {
  return !!(
    options.closeSidebarOnApply
    || (
      snapshot?.sessionId
      && sessionState.activeSessionLoad?.sessionId === snapshot.sessionId
      && sessionState.activeSessionLoad.closeSidebarOnApply
    )
  );
}

// ============================================================
// Session loading overlay
// ============================================================

function getSessionLoadLabel(sessionId: string): string {
  const meta = sessionId ? getSessionMeta(sessionId) : null;
  const title = meta?.title ? `"${meta.title}"` : '所选会话';
  return `正在载入 ${title} 的完整消息记录…`;
}

export function setSessionLoading(sessionId: string | null, options: any = {}): void {
  const loading = !!sessionId;
  const blocking = options.blocking !== false;
  sessionState.activeSessionLoad = loading
    ? {
      sessionId,
      blocking,
      context: options.context || null,
      closeSidebarOnApply: !!options.closeSidebarOnApply,
      snapshot: null,
    }
    : null;
  const showOverlay = !!(loading && blocking);
  const overlay = getSessionLoadingOverlay();
  const labelEl = getSessionLoadingLabelEl();
  const msgInput = getMsgInput();
  const modeSelect = getModeSelect();
  const sendBtn = getSendBtn();
  const abortBtn = getAbortBtn();
  document.body.classList.toggle('session-loading-active', showOverlay);
  logSessionLoadDebug('load:set-session-loading', {
    sessionId: sessionId || null,
    loading,
    blocking,
    showOverlay,
    label: loading ? (options.label || getSessionLoadLabel(sessionId!)) : null,
    contextReason: options.context?.reason || null,
    closeSidebarOnApply: !!options.closeSidebarOnApply,
    activeLoadSessionId: sessionState.activeSessionLoad?.sessionId || null,
    currentSessionId: sessionState.currentSessionId || null,
  });
  logSessionSwitch('set-loading', {
    sessionId,
    loading,
    blocking,
    showOverlay,
    label: loading ? (options.label || getSessionLoadLabel(sessionId!)) : null,
  });
  if (overlay) {
    overlay.hidden = !showOverlay;
    overlay.setAttribute('aria-hidden', showOverlay ? 'false' : 'true');
  }
  if (labelEl) labelEl.textContent = loading ? (options.label || getSessionLoadLabel(sessionId!)) : '正在整理消息与上下文…';
  if (msgInput) msgInput.disabled = showOverlay;
  if (modeSelect) modeSelect.disabled = showOverlay;
  if (sendBtn) sendBtn.disabled = showOverlay;
  if (abortBtn) abortBtn.disabled = showOverlay;
  if (showOverlay && document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
}

export function clearSessionLoading(sessionId?: string): void {
  if (sessionId && sessionState.activeSessionLoad && sessionState.activeSessionLoad.sessionId !== sessionId) return;
  logSessionSwitch('clear-loading', {
    sessionId: sessionId || null,
    activeLoadSessionId: sessionState.activeSessionLoad?.sessionId || null,
  });
  setSessionLoading(null, { blocking: false });
}

export function isBlockingSessionLoad(sessionId?: string): boolean {
  return !!(
    sessionState.activeSessionLoad &&
    sessionState.activeSessionLoad.blocking &&
    (!sessionId || sessionState.activeSessionLoad.sessionId === sessionId)
  );
}

// ============================================================
// Session switch flow
// ============================================================

export function finishSessionSwitch(sessionId: string): void {
  const shouldFocusComposer = !!sessionState.activeSessionLoad?.context?.focusComposerAfterLoad;
  logComposerFocusDebug('finish-switch', {
    sessionId,
    shouldFocusComposer,
    blocking: isBlockingSessionLoad(sessionId),
    activeLoadSessionId: sessionState.activeSessionLoad?.sessionId || null,
  });
  if (isBlockingSessionLoad(sessionId)) {
    _scrollToBottom?.();
    requestAnimationFrame(() => {
      clearSessionLoading(sessionId);
      if (shouldFocusComposer) focusComposerInput();
    });
    return;
  }
  clearSessionLoading(sessionId);
  if (shouldFocusComposer) focusComposerInput();
}

export function finalizeLoadedSession(sessionId: string): void {
  logSessionSwitch('finalize-loaded', {
    sessionId,
    hasSnapshot: !!sessionState.activeSessionLoad?.snapshot,
    messageCount: Array.isArray(sessionState.activeSessionLoad?.snapshot?.messages) ? sessionState.activeSessionLoad.snapshot.messages.length : 0,
    snapshotComplete: !!sessionState.activeSessionLoad?.snapshot?.complete,
  });
  if (sessionState.activeSessionLoad?.sessionId === sessionId && sessionState.activeSessionLoad.snapshot) {
    sessionState.activeSessionLoad.snapshot.complete = true;
    cacheSessionSnapshot(sessionState.activeSessionLoad.snapshot);
  }
  finishSessionSwitch(sessionId);
}

function isUserInitiatedSessionNavigation(options: any = {}): boolean {
  return !!(options.focusComposer || options.closeSidebarOnApply);
}

function consumeSidebarCloseIntent(options: any = {}): boolean {
  const shouldCloseSidebarImmediately = !!options.closeSidebarOnApply;
  if (shouldCloseSidebarImmediately) _closeSidebar?.();
  return shouldCloseSidebarImmediately;
}

function beginSessionSwitch(sessionId: string, options: any = {}): void {
  if (!sessionId) return;
  const blocking = options.blocking !== false;
  const force = options.force === true;
  logSessionSwitch('begin', {
    sessionId,
    blocking,
    force,
    loadContextReason: options.context?.reason || null,
    currentSessionId: sessionState.currentSessionId,
    activeLoadSessionId: sessionState.activeSessionLoad?.sessionId || null,
  });
  logSidebarSessionDebug('begin-switch', {
    sessionId,
    blocking,
    force,
    closeSidebarOnApply: !!options.closeSidebarOnApply,
    loadContextReason: options.context?.reason || null,
    currentSessionId: sessionState.currentSessionId || null,
    activeLoadSessionId: sessionState.activeSessionLoad?.sessionId || null,
  });
  if (!force && sessionState.activeSessionLoad?.sessionId === sessionId) {
    const sidebarClosedImmediately = consumeSidebarCloseIntent(options);
    logSidebarSessionDebug('begin-switch-active-return', {
      sessionId,
      closeSidebarOnApply: !!options.closeSidebarOnApply,
      activeLoadCloseSidebarOnApply: !!sessionState.activeSessionLoad.closeSidebarOnApply,
      sidebarClosedImmediately,
    });
    return;
  }
  if (!force && sessionId === sessionState.currentSessionId && !sessionState.activeSessionLoad) return;
  if (consumeSidebarCloseIntent(options)) {
    logSidebarSessionDebug('begin-switch-request-close', {
      sessionId,
      closeSidebarOnApply: true,
      activeLoadSessionId: sessionState.activeSessionLoad?.sessionId || null,
    });
  }
  logSessionLoadDebug('load:begin-session-switch', {
    sessionId,
    blocking,
    force,
    loadContextReason: options.context?.reason || null,
    currentSessionId: sessionState.currentSessionId || null,
    activeLoadSessionId: sessionState.activeSessionLoad?.sessionId || null,
  });
  _incrementRenderEpoch?.();
  sessionState.loadedHistorySessionId = null;
  setSessionLoading(sessionId, {
    blocking,
    label: options.label,
    context: options.context || null,
    closeSidebarOnApply: false,
  });
  send({ type: 'load_session', sessionId });
}

function showCachedSession(sessionId: string, options: any = {}): boolean {
  const snapshot = buildCachedSessionSnapshot(sessionId);
  if (!snapshot) {
    logSessionSwitch('show-cached-miss', { sessionId });
    return false;
  }
  logSessionSwitch('show-cached-hit', {
    sessionId,
    messageCount: Array.isArray(snapshot.messages) ? snapshot.messages.length : 0,
    updated: snapshot.updated ?? null,
    isRunning: !!snapshot.isRunning,
  });
  logSidebarSessionDebug('show-cached-apply', {
    sessionId,
    currentSessionId: sessionState.currentSessionId || null,
    messageCount: Array.isArray(snapshot.messages) ? snapshot.messages.length : 0,
    isRunning: !!snapshot.isRunning,
    closeSidebarOnApply: !!options.closeSidebarOnApply,
  });
  if (sessionState.currentSessionId && sessionState.currentSessionId !== sessionId) {
    logSessionFlow('show-cached:detach-previous-view', {
      previousSessionId: sessionState.currentSessionId,
      nextSessionId: sessionId,
    });
    send({ type: 'detach_view' });
  }
  clearSessionLoading();
  touchSessionCache(sessionId);
  applySessionSnapshot(snapshot, {
    immediate: true,
    suppressUnreadToast: true,
    closeSidebarOnApply: !!options.closeSidebarOnApply,
  });
  return true;
}

export function openSession(sessionId: string, options: any = {}): void {
  if (!sessionId) return;
  logSessionSwitch('open', {
    sessionId,
    options,
    currentSessionId: sessionState.currentSessionId,
  });
  if (options.forceSync) {
    beginSessionSwitch(sessionId, {
      blocking: options.blocking !== false,
      force: true,
      label: options.label,
      context: options.context || null,
      closeSidebarOnApply: !!options.closeSidebarOnApply,
    });
    return;
  }
  if (!options.force && sessionId === sessionState.currentSessionId && !sessionState.activeSessionLoad) {
    logSidebarSessionDebug('open-session-current-return', {
      sessionId,
      closeSidebarOnApply: !!options.closeSidebarOnApply,
      willCloseSidebar: !!options.closeSidebarOnApply,
    });
    consumeSidebarCloseIntent(options);
    return;
  }

  const disposition = getSessionCacheDisposition(sessionId);
  const messagesDiv = getMessagesDiv();
  const hasRenderedContent = !!messagesDiv && messagesDiv.children.length > 0;
  const requestedBlocking = options.blocking !== false;
  const isUserInitiatedNavigation = isUserInitiatedSessionNavigation(options);
  const shouldDowngradeMissToNonBlocking = !!(
    !isUserInitiatedNavigation
    && disposition === 'miss'
    && requestedBlocking
    && sessionState.currentSessionId
    && sessionState.currentSessionId !== sessionId
    && hasRenderedContent
  );
  const effectiveBlocking = shouldDowngradeMissToNonBlocking ? false : requestedBlocking;
  logSidebarSessionDebug('open-session-start', {
    sessionId,
    disposition,
    force: !!options.force,
    forceSync: !!options.forceSync,
    blocking: effectiveBlocking,
    requestedBlocking,
    downgradedMissToNonBlocking: shouldDowngradeMissToNonBlocking,
    userInitiatedNavigation: isUserInitiatedNavigation,
    focusComposer: !!options.focusComposer,
    closeSidebarOnApply: !!options.closeSidebarOnApply,
    hasRenderedContent,
    currentSessionId: sessionState.currentSessionId || null,
    activeLoadSessionId: sessionState.activeSessionLoad?.sessionId || null,
  });
  logSessionLoadDebug('load:open-session', {
    sessionId,
    disposition,
    force: !!options.force,
    forceSync: !!options.forceSync,
    blocking: effectiveBlocking,
    requestedBlocking,
    downgradedMissToNonBlocking: shouldDowngradeMissToNonBlocking,
    userInitiatedNavigation: isUserInitiatedNavigation,
    focusComposer: !!options.focusComposer,
    hasRenderedContent,
    currentSessionId: sessionState.currentSessionId || null,
    activeLoadSessionId: sessionState.activeSessionLoad?.sessionId || null,
  });
  logSessionFlow('open-session:disposition', {
    sessionId,
    disposition,
    force: !!options.force,
    forceSync: !!options.forceSync,
    blocking: effectiveBlocking,
    requestedBlocking,
    downgradedMissToNonBlocking: shouldDowngradeMissToNonBlocking,
    userInitiatedNavigation: isUserInitiatedNavigation,
    hasRenderedContent,
    currentSessionId: sessionState.currentSessionId,
    activeLoadSessionId: sessionState.activeSessionLoad?.sessionId || null,
  });
  if (disposition === 'strong') {
    showCachedSession(sessionId, options);
    if (options.focusComposer) {
      logComposerFocusDebug('open-session:focus-after-strong-cache', { sessionId });
      focusComposerInput();
    }
    return;
  }
  if (disposition === 'weak' && showCachedSession(sessionId, options)) {
    if (options.focusComposer) {
      logComposerFocusDebug('open-session:focus-after-weak-cache', { sessionId });
      focusComposerInput();
    }
    beginSessionSwitch(sessionId, {
      blocking: false,
      force: true,
      label: options.label,
      context: {
        ...(options.context || {}),
        focusComposerAfterLoad: !!options.focusComposer,
      },
      closeSidebarOnApply: !!options.closeSidebarOnApply,
    });
    return;
  }
  beginSessionSwitch(sessionId, {
    blocking: effectiveBlocking,
    force: options.force === true,
    label: options.label,
    context: {
      ...(options.context || {}),
      focusComposerAfterLoad: !!options.focusComposer,
    },
    closeSidebarOnApply: !!options.closeSidebarOnApply,
  });
}
